import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  NotfallBuchungCreateRequest,
  NotfallBuchungCreateResponse,
  NotfallBuchungStatusDto,
  NotfallKategorieDto,
  NotfallVerfuegbarkeitDto,
} from '../models/notfall.model';

const KATEGORIEN_KEY = makeStateKey<NotfallKategorieDto[]>('notfall.kategorien');

// SSR ruft die API über den Docker-internen Hostnamen — der Browser geht
// durch den /api-Proxy. Identisches Muster wie GewerkService.
const SSR_API_BASE = 'http://api:8080';

@Injectable({ providedIn: 'root' })
export class NotfallService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Liste aller aktiven Notfall-Kategorien inkl. aktuellem Slot-Festpreis.
   * SSR-fähig via TransferState — der Slot wird serverseitig berechnet, der
   * Cache ist daher prinzipiell zeit-sensitiv. Akzeptable Toleranz, weil ein
   * Slot-Wechsel nur 4× am Tag passiert und der Hydration-Path im Worst Case
   * zwei Sekunden falsche Festpreise zeigt.
   */
  getKategorien(): Observable<NotfallKategorieDto[]> {
    if (isPlatformBrowser(this.platformId)) {
      if (this.transferState.hasKey(KATEGORIEN_KEY)) {
        const cached = this.transferState.get(KATEGORIEN_KEY, []);
        this.transferState.remove(KATEGORIEN_KEY);
        return of(cached);
      }
      return this.http.get<NotfallKategorieDto[]>('/api/notfall/kategorien');
    }

    return this.http.get<NotfallKategorieDto[]>(`${SSR_API_BASE}/api/notfall/kategorien`).pipe(
      tap((data) => this.transferState.set(KATEGORIEN_KEY, data)),
    );
  }

  /**
   * Live-Verfügbarkeit für eine konkrete Kategorie + PLZ. Bewusst nicht
   * SSR-gecached — der Wert muss zum Klick-Zeitpunkt frisch sein.
   */
  getVerfuegbarkeit(slug: string, plz: string): Observable<NotfallVerfuegbarkeitDto> {
    const params = new HttpParams()
      .set('kategorie', slug)
      .set('plz', plz);
    return this.http.get<NotfallVerfuegbarkeitDto>('/api/notfall/verfuegbarkeit', { params });
  }

  /** Legt eine Notfall-Buchung an und erzeugt den Stripe-PaymentIntent. */
  createBuchung(request: NotfallBuchungCreateRequest): Observable<NotfallBuchungCreateResponse> {
    return this.http.post<NotfallBuchungCreateResponse>('/api/notfall/buchung', request);
  }

  /** Status-Polling für die Live-Status-Seite. */
  getBuchungStatus(notfallBuchungId: string): Observable<NotfallBuchungStatusDto> {
    return this.http.get<NotfallBuchungStatusDto>(`/api/notfall/buchung/${notfallBuchungId}/status`);
  }
}
